import { Form, Formik } from 'formik';
import * as bd from 'react-basic-design';
import { UserAgentDTO } from '../../../api/user-agent-api';
import { UserSessionDTO } from '../../../api/user-session-api';
import SvgClose from '../../../assets/icons/Close';
import SvgDelete from '../../../assets/icons/Delete';
import SvgSave from '../../../assets/icons/Save';
import { BasicModal } from '../../../components/basic/basic-modal';
import { T } from '../../../components/basic/text';
import { yup } from '../../../components/basic/yup';
import { FormikCellToggle, FormikDateInput, FormikTextField } from '../../../components/formik';

interface SaveUserSessionModalProps {
    show: boolean;
    allowEdit: boolean;
    selectedUserSession?: UserSessionDTO;
    userAgents: UserAgentDTO[];
    onHide: () => void;
    onSave: (values: UserSessionDTO) => void;
    onDelete?: (userSession: UserSessionDTO) => void;
}

export function SaveUserSessionModal({
    show,
    allowEdit,
    selectedUserSession,
    userAgents,
    onHide,
    onSave,
    onDelete,
}: SaveUserSessionModalProps) {
    const UserSessionSchema = yup.object().shape({
        userName: yup.string(),
        refreshToken: yup.string(),
        refreshCount: yup.number().required(' '),
        refreshTokenDate: yup.date().required(' '),
        lastIP: yup.string(),
        ipList: yup.string(),
        ip: yup.string(),
        userAgentId: yup.number().required(' '),
        isDeleted: yup.boolean().required(' '),
    });

    const defaultValues: UserSessionDTO = {
        id: selectedUserSession?.id,

        userName: selectedUserSession ? selectedUserSession.userName : '',
        refreshToken: selectedUserSession ? selectedUserSession.refreshToken : '',
        refreshCount: selectedUserSession ? selectedUserSession.refreshCount : 0,
        refreshTokenDate: selectedUserSession ? selectedUserSession.refreshTokenDate : new Date(),

        lastIP: selectedUserSession ? selectedUserSession.lastIP : '',
        ipList: selectedUserSession ? selectedUserSession.ipList : '',
        ip: selectedUserSession ? selectedUserSession.ip : '',
        userAgentId: selectedUserSession ? selectedUserSession.userAgentId : 0,
        isDeleted: selectedUserSession ? selectedUserSession.isDeleted : true,
    };

    if (!show) return <></>;

    return (
        <Formik
            initialValues={defaultValues}
            validationSchema={UserSessionSchema}
            onSubmit={onSave}
        >
            {formik => (
                <BasicModal
                    show={show}
                    onHide={onHide}
                    size="xl"
                    title={
                        <bd.Flex className="w-100" align="center" content="between">
                            <T>{selectedUserSession ? 'edit-user-session' : 'new-user-session'}</T>
                            <bd.Flex align="center" gap={2}>
                                <bd.Button
                                    variant="icon"
                                    size="sm"
                                    onClick={formik.submitForm}
                                    disabled={!allowEdit}
                                >
                                    <SvgSave />
                                </bd.Button>
                                {selectedUserSession && onDelete && (
                                    <bd.Button
                                        disabled={!allowEdit}
                                        variant="icon"
                                        color="secondary"
                                        size="sm"
                                        onClick={() => onDelete(selectedUserSession)}
                                    >
                                        <SvgDelete />
                                    </bd.Button>
                                )}
                                <bd.Button
                                    variant="icon"
                                    color="secondary"
                                    size="sm"
                                    onClick={onHide}
                                >
                                    <SvgClose />
                                </bd.Button>
                            </bd.Flex>
                        </bd.Flex>
                    }
                >
                    <Form>
                        <bd.Flex align="center" wrap>
                            <FormikTextField
                                label="user-name"
                                name="userName"
                                readOnly={!allowEdit}
                                width="11rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="refresh-token"
                                name="refreshToken"
                                readOnly={!allowEdit}
                                width="11rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="refresh-count"
                                name="refreshCount"
                                readOnly={!allowEdit}
                                width="11rem"
                                handleEnter
                            />

                            <FormikDateInput
                                label="refresh-token-date"
                                name="refreshTokenDate"
                                readOnly={!allowEdit}
                                inputMaxWidth="11rem"
                            />
                            <FormikTextField
                                label="last-ip"
                                name="lastIP"
                                readOnly={!allowEdit}
                                width="11rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="ip-list"
                                name="ipList"
                                readOnly={!allowEdit}
                                width="11rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="ip"
                                name="ip"
                                readOnly={!allowEdit}
                                width="11rem"
                                handleEnter
                            />

                            <FormikTextField
                                label="user-agent-id"
                                name="userAgentId"
                                readOnly={!allowEdit}
                                type="select"
                                items={[
                                    { id: null, title: '' },
                                    ...userAgents.map(userAgent => ({
                                        id: userAgent.id,
                                        title: `${userAgent.os} `,
                                    })),
                                ]}
                                width="11rem"
                                handleEnter
                            />

                            <FormikCellToggle
                                label={<T>is-deleted</T>}
                                name="isDeleted"
                                readOnly={!allowEdit}
                            />
                        </bd.Flex>
                    </Form>
                </BasicModal>
            )}
        </Formik>
    );
}
